import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Plus, X, CheckCircle, ArrowRight } from 'lucide-react'
import { engineApiClient } from '@/lib/apiClient'
import { trackEvent } from '@/lib/monitoring'

const WHAT_NEXT = [
  { step: 1, title: 'Upload documents',    desc: 'Add JDs and context files' },
  { step: 2, title: 'Run AI conversation', desc: 'Erica captures role details' },
  { step: 3, title: 'Confirm & generate',  desc: 'Review and get polished outputs' },
]

export const NewProject = () => {
  const navigate = useNavigate()

  const [projectName, setProjectName]       = useState('')
  const [company, setCompany]               = useState('')
  const [roleTitle, setRoleTitle]           = useState('')
  const [primaryGoal, setPrimaryGoal]       = useState('')
  const [teamMembers, setTeamMembers]       = useState<string[]>([])
  const [newMember, setNewMember]           = useState('')
  const [isSubmitting, setIsSubmitting]     = useState(false)
  const [savedFeedback, setSavedFeedback]   = useState(false)
  const [errors, setErrors]                 = useState<{ roleTitle?: string; form?: string }>({})

  const addMember = () => {
    const trimmed = newMember.trim()
    if (trimmed && !teamMembers.includes(trimmed)) {
      setTeamMembers((prev) => [...prev, trimmed])
    }
    setNewMember('')
  }

  const validate = () => {
    const errs: typeof errors = {}
    if (!roleTitle.trim()) errs.roleTitle = 'Role title is required'
    setErrors(errs)
    return Object.keys(errs).length === 0
  }

  const buildPayload = () => ({
    title: projectName.trim()
      ? projectName.trim()
      : company.trim()
        ? `${roleTitle.trim()}, ${company.trim()}`
        : roleTitle.trim(),
    company: company.trim(),
    regionFocus: undefined,
    owner: 'Sax',
    collaborators: teamMembers,
  })

  const handleContinue = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!validate()) return
    setIsSubmitting(true)
    try {
      const project = await engineApiClient.createProject(buildPayload())
      trackEvent('project_created', { title: project.title })
      navigate(`/project/${project.id}/upload`)
    } catch {
      setErrors({ form: 'Failed to create project. Please try again.' })
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleSaveDraft = async () => {
    if (!validate()) return
    setIsSubmitting(true)
    try {
      await engineApiClient.createProject(buildPayload())
      setSavedFeedback(true)
      setTimeout(() => setSavedFeedback(false), 2000)
    } catch {
      setErrors({ form: 'Failed to save. Please try again.' })
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <div className="bg-[#02182B] min-h-screen">
      {/* Page header */}
      <div className="border-b border-[#233D4C]">
        <div className="max-w-7xl mx-auto px-6 py-6">
          <h1 className="text-2xl font-bold text-white">New Project</h1>
          <p className="text-gray-400 text-sm mt-1">Set up a new search project to begin the intake flow</p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8 flex gap-8">

        {/* Main form */}
        <form onSubmit={handleContinue} className="flex-1 min-w-0 space-y-6">

          {/* Project basics */}
          <div className="bg-[#233D4C] border border-[#2a4a5c] rounded-lg p-5 space-y-4">
            <h2 className="text-white font-semibold">Project Basics</h2>
            <div className="space-y-3">
              <label className="block space-y-1.5">
                <span className="text-sm font-medium text-white">Project Name</span>
                <input
                  type="text"
                  value={projectName}
                  onChange={(e) => setProjectName(e.target.value)}
                  placeholder="e.g. VP Sales Search — AlphaGrid"
                  className="w-full bg-[#02182B] border border-[#2a4a5c] rounded-lg px-4 py-3 text-white placeholder-gray-500 text-sm focus:outline-none focus:border-[#FD802E] transition-colors"
                />
              </label>
              <div className="grid sm:grid-cols-2 gap-3">
                <label className="block space-y-1.5">
                  <span className="text-sm font-medium text-white">Company Name</span>
                  <input
                    type="text"
                    value={company}
                    onChange={(e) => setCompany(e.target.value)}
                    placeholder="Company name"
                    className="w-full bg-[#02182B] border border-[#2a4a5c] rounded-lg px-4 py-3 text-white placeholder-gray-500 text-sm focus:outline-none focus:border-[#FD802E] transition-colors"
                  />
                </label>
                <label className="block space-y-1.5">
                  <span className="text-sm font-medium text-white">Role Title *</span>
                  <input
                    type="text"
                    value={roleTitle}
                    onChange={(e) => setRoleTitle(e.target.value)}
                    placeholder="e.g. VP Sales"
                    className={`w-full bg-[#02182B] border rounded-lg px-4 py-3 text-white placeholder-gray-500 text-sm focus:outline-none transition-colors ${
                      errors.roleTitle ? 'border-red-400' : 'border-[#2a4a5c] focus:border-[#FD802E]'
                    }`}
                  />
                  {errors.roleTitle && <p className="text-xs text-red-400">{errors.roleTitle}</p>}
                </label>
              </div>
            </div>
          </div>

          {/* Primary goal */}
          <div className="bg-[#233D4C] border border-[#2a4a5c] rounded-lg p-5 space-y-3">
            <h2 className="text-white font-semibold">Primary Goal</h2>
            <textarea
              value={primaryGoal}
              onChange={(e) => setPrimaryGoal(e.target.value)}
              placeholder="Describe the main objective of this search — e.g. 'Find a VP Sales to build and lead the EMEA revenue function from scratch, targeting Series B–D SaaS companies.'"
              rows={4}
              className="w-full bg-[#02182B] border border-[#2a4a5c] rounded-lg px-4 py-3 text-white placeholder-gray-500 text-sm focus:outline-none focus:border-[#FD802E] transition-colors resize-none leading-relaxed"
            />
          </div>

          {/* Team members */}
          <div className="bg-[#233D4C] border border-[#2a4a5c] rounded-lg p-5 space-y-3">
            <h2 className="text-white font-semibold">Team Members</h2>
            {teamMembers.length > 0 && (
              <ul className="space-y-2">
                {teamMembers.map((member) => (
                  <li key={member} className="flex items-center justify-between bg-[#02182B] border border-[#2a4a5c] rounded-lg px-3 py-2">
                    <span className="text-white text-sm">{member}</span>
                    <button
                      type="button"
                      onClick={() => setTeamMembers((prev) => prev.filter((m) => m !== member))}
                      className="text-gray-400 hover:text-red-400 transition-colors"
                    >
                      <X size={13} />
                    </button>
                  </li>
                ))}
              </ul>
            )}
            <div className="flex gap-2">
              <input
                type="text"
                value={newMember}
                onChange={(e) => setNewMember(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addMember())}
                placeholder="Add team member name or email"
                className="flex-1 bg-[#02182B] border border-[#2a4a5c] rounded-lg px-3 py-2 text-white placeholder-gray-500 text-sm focus:outline-none focus:border-[#FD802E] transition-colors"
              />
              <button
                type="button"
                onClick={addMember}
                className="flex items-center gap-1.5 bg-[#02182B] border border-[#2a4a5c] text-white text-sm px-3 py-2 rounded-lg hover:border-[#FD802E]/40 transition-colors"
              >
                <Plus size={14} /> Add
              </button>
            </div>
          </div>

          {errors.form && (
            <p className="text-sm text-red-400">{errors.form}</p>
          )}

          {/* Form actions (mobile / bottom) */}
          <div className="flex items-center justify-between gap-3 lg:hidden">
            {savedFeedback && <span className="text-green-400 text-sm">Draft saved ✓</span>}
            <div className="flex gap-2 ml-auto">
              <button
                type="button"
                onClick={handleSaveDraft}
                disabled={isSubmitting}
                className="bg-[#233D4C] border border-[#2a4a5c] text-white text-sm px-4 py-2 rounded-lg hover:bg-[#2a4a5c] transition-colors disabled:opacity-50"
              >
                Save Draft
              </button>
              <button
                type="submit"
                disabled={isSubmitting}
                className="bg-[#FD802E] text-[#233D4C] font-semibold text-sm px-5 py-2 rounded-lg hover:bg-[#ff8f45] transition-colors disabled:opacity-50"
              >
                {isSubmitting ? 'Creating…' : 'Continue'}
              </button>
            </div>
          </div>

        </form>

        {/* Right sidebar */}
        <div className="w-80 flex-shrink-0 space-y-4 hidden lg:block">

          {/* What happens next */}
          <div className="bg-[#233D4C] border border-[#2a4a5c] rounded-lg p-5 space-y-4">
            <p className="text-xs uppercase text-gray-400 font-medium">What Happens Next</p>
            <ol className="space-y-4">
              {WHAT_NEXT.map((item) => (
                <li key={item.step} className="flex items-start gap-3">
                  <span className="w-6 h-6 bg-[#FD802E]/10 text-[#FD802E] text-xs font-bold rounded-full flex items-center justify-center shrink-0 mt-0.5">
                    {item.step}
                  </span>
                  <div>
                    <p className="text-white text-sm font-medium">{item.title}</p>
                    <p className="text-gray-400 text-xs mt-0.5">{item.desc}</p>
                  </div>
                </li>
              ))}
            </ol>
          </div>

          {/* Actions */}
          <div className="space-y-2">
            {savedFeedback && (
              <p className="text-green-400 text-sm text-center">Draft saved ✓</p>
            )}
            <button
              type="submit"
              form=""
              onClick={(e) => { e.preventDefault(); handleContinue(e as unknown as React.FormEvent) }}
              disabled={isSubmitting}
              className="w-full flex items-center justify-center gap-2 bg-[#FD802E] text-[#233D4C] font-semibold py-3 rounded-lg hover:bg-[#ff8f45] transition-colors disabled:opacity-50"
            >
              Continue <ArrowRight size={16} />
            </button>
            <button
              type="button"
              onClick={handleSaveDraft}
              disabled={isSubmitting}
              className="w-full text-gray-400 hover:text-white text-sm py-2 transition-colors"
            >
              Save Draft
            </button>
          </div>

        </div>
      </div>
    </div>
  )
}
